import { useEffect, useRef, useState } from 'react';
import { useDemande } from '../../components/Dialogue.jsx';
import { Shell } from '../../components/layout/Shell.jsx';
import { PhotoBtn } from '../../components/PhotoBtn.jsx';
import { QRCode } from '../../components/QRCode.jsx';
import { SignaturePad } from '../../components/SignaturePad.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { FiligraneControls } from '../../components/ui/FiligraneControls.jsx';
import { Inp, NumInput, _inp } from '../../components/ui/Inputs.jsx';
import { SaveBar } from '../../components/ui/SaveBar.jsx';
import { Tabs } from '../../components/ui/Tabs.jsx';
import { SOC, TPL_DEVIS_DEF, TPL_FACTURE_DEF } from '../../constants/company';
import { LANGS_REG, LANGUE_DU_PAYS, PAYS, PAYS_BY } from '../../constants/countries';
import { SYSTEMES_QR, systemeQrDuPays } from '../../constants/paiements-qr';
import { societeApi } from '../../api/resources';
import { prepareBulletin } from '../../lib/bulletin-banque';
import { reduisImageQr } from '../../lib/image-qr';
import { DEVIS_STYLES } from '../../constants/documents';
import { avecSansDoublon, metierCats, metierRayons, metierUnites } from '../../constants/trades';
import { modulesSouscrits } from '../../lib/modules';
import { DocumentA4 } from '../documents/DocumentA4.jsx';
import { useSession } from '../../api/session.jsx';
import { useModeConnexion } from '../../api/modeConnexion';
import { BanniereCard } from './BanniereCard.jsx';
import { BoiteEnvoiCard } from './BoiteEnvoiCard.jsx';
import { ComptesEquipeCard } from './ComptesEquipeCard.jsx';
import { MaSignatureCard } from './MaSignatureCard.jsx';
import { AccesCreateurCard } from './AccesCreateurCard.jsx';
import { MonCompteCard } from './MonCompteCard.jsx';
import { CHOIX_POLICES, pileDePolice } from '../../lib/polices';
import { EnvoisAutoCard } from './EnvoisAutoCard.jsx';
import { TextesRelancesCard } from './TextesRelancesCard.jsx';
import { LogoDuDocument } from './LogoDuDocument.jsx';
import { accentDuDocument, offreDExemple } from './offre-d-exemple';
import { StyleDesDocuments } from './StyleDesDocuments.jsx';
import { DefautsDocuments } from './DefautsDocuments.jsx';
import { MesSocietesCard } from './MesSocietesCard.jsx';
import { PackIaCard } from './PackIaCard.jsx';
import { RepriseCard } from './RepriseCard.jsx';
import { SecteurCard } from './SecteurCard.jsx';
import { TauxTvaCard } from './TauxTvaCard.jsx';
import { PointageCard } from './PointageCard.jsx';
import { AdressesTravailCard } from '../lieux/AdressesTravailCard.jsx';
import { TamponImport } from './TamponImport.jsx';
import { SORTES_TAMPON, tamponVoulu } from '../../lib/tampon';
import { RepartirAZeroCard } from './RepartirAZeroCard.jsx';
import { UnitesCard } from './UnitesCard.jsx';
import { idProvisoire } from '../../lib/identifiants';
import { telechargeLaSauvegarde } from '../../lib/sauvegarde';
import { codePinSaisi, membresSansCode } from '../../lib/personnel';
import { EditeurCharges } from '../../components/EditeurCharges.jsx';
import { ROLE_DESC } from '../../lib/access';
import { setDeviseGlobale, setPaysMontants } from '../../lib/currency';
import { setLangGlobale, tr } from '../../lib/i18n';
import { LANGUES_TRADUITES } from '../../lib/traduction-ecran';
import { RESEAUX } from '../../lib/payments';
import { swissQR } from '../../lib/swiss-qr';
import { docRef, isQRIban } from '../../lib/swiss-refs';
import { uid } from '../../lib/utils';
import { tauxTvaProposes } from '../../lib/tva';
import { METHODES_TVA } from '../../lib/vat';
import { ongletReglagesDemande, oublieOngletReglages } from '../../lib/onglet-reglages';
import { C, DA, EMP_COLORS } from '../../theme/palette';

export function ReglagesPage({
  onNav,
  soc,
  setSoc,
  employes,
  setEmployes,
  estMixte,
  univers,
  setUnivers,
  onForceRefresh,
  vue,
  setVue,
}) {
  const demande = useDemande();
  const { session } = useSession();
  // Le code de la société et le code PIN ne servent que tant que l'ouvrier
  // entre encore par « Code ouvrier ».
  const codeOuvrier = useModeConnexion()?.codeOuvrier === true;
  // Un autre écran peut demander un onglet : « Ajouter mon IBAN » ouvre la Banque.
  const [tab, setTab] = useState(() => ongletReglagesDemande('societe'));
  useEffect(() => oublieOngletReglages(), []);
  const [accForm, setAccForm] = useState(null);
  const [paysQ, setPaysQ] = useState('');
  const [styleOuvert, setStyleOuvert] = useState(false);
  const [secteurEdit, setSecteurEdit] = useState(univers === 'magasin' ? 'magasin' : 'chantier');
  const set = (patch) => setSoc((s) => ({ ...s, ...patch }));
  // Chaque pays a son système de QR code bancaire, ou n'en a pas (voir
  // constants/paiements-qr) : deux choix, « QR code + IBAN » ou « IBAN
  // uniquement » ; sans système dans le pays, l'IBAN seul.
  const systemePays = systemeQrDuPays(soc?.pays || 'CH');
  const infosQr = systemePays ? SYSTEMES_QR[systemePays] : null;
  // « QR-facture » et « Les deux », anciens réglages, valent « QR code + IBAN ».
  // « Bulletin de ma banque » : le bulletin remis par la banque, tel quel.
  const modePaiement =
    soc?.paiement === 'bulletin'
      ? 'bulletin'
      : !systemePays || soc?.paiement === 'iban'
        ? 'iban'
        : 'deux';
  const [erreurQr, setErreurQr] = useState('');
  const [erreurBulletin, setErreurBulletin] = useState('');
  const [lectureBulletin, setLectureBulletin] = useState(false);
  const champBulletin = useRef(null);
  // Le bulletin remis par la banque : SwissTech lit son QR code (compte et
  // titulaire) ; illisible, il est imprimé tel quel.
  const joinsBulletin = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    setLectureBulletin(true);
    try {
      const bulletinImage = await prepareBulletin(f);
      const lu = await societeApi.lisBulletin(bulletinImage).catch(() => null);
      const patch = { bulletinImage, bulletinLu: lu && lu.iban ? lu : null };
      const compact = (v) =>
        String(v || '')
          .replace(/\s/g, '')
          .toUpperCase();
      if (lu && lu.iban && compact(lu.iban) !== compact(soc?.iban)) {
        const ibanLu = compact(lu.iban)
          .replace(/(.{4})/g, '$1 ')
          .trim();
        if (
          !compact(soc?.iban) ||
          window.confirm(
            'Le bulletin porte l’IBAN ' + ibanLu + '. Le mettre à la place de ' + soc.iban + ' ?',
          )
        ) {
          patch.iban = ibanLu;
        }
      }
      set(patch);
      setErreurBulletin('');
    } catch (err) {
      setErreurBulletin(
        'Ce fichier ne peut pas être lu. Essaie le PDF de la banque ou une photo nette du bulletin.',
      );
    } finally {
      setLectureBulletin(false);
    }
  };
  // Cet écran reçoit la société telle qu'enregistrée (le Mix reste « mixte ») :
  // les droits sur les chantiers ne se règlent que si elle tient le bâtiment.
  const avecBatiment = modulesSouscrits(soc?.secteur).batiment;
  const [resOk, setResOk] = useState(false);
  const [paysSel, setPaysSel] = useState(null);
  const [paysOk, setPaysOk] = useState(false);
  const AF = soc?.afficher || {};
  const setAF = (k, v) => setSoc((s) => ({ ...s, afficher: { ...(s.afficher || {}), [k]: v } }));
  // Coordonnées par secteur (mode Mix) : sinon coordonnées uniques
  const profil = (soc?.profils || {})[secteurEdit] || {};
  const coord = estMixte ? { ...soc, ...profil } : soc;
  const setCoord = (patch) => {
    if (estMixte) {
      setSoc((s) => ({
        ...s,
        profils: {
          ...(s.profils || {}),
          [secteurEdit]: { ...((s.profils || {})[secteurEdit] || {}), ...patch },
        },
      }));
    } else {
      set(patch);
    }
  };
  const SectPicker = () =>
    estMixte ? (
      <div
        style={{ background: C.navyXL, borderRadius: 10, padding: '10px 12px', marginBottom: 12 }}
      >
        <div
          style={{
            fontSize: 8.5,
            fontWeight: 700,
            color: C.muted,
            textTransform: 'uppercase',
            marginBottom: 6,
          }}
        >
          Coordonnées pour le secteur
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {[
            ['chantier', '🏗 Bâtiment'],
            ['magasin', '🛒 Magasin'],
          ].map(([s, lb]) => {
            const on = secteurEdit === s;
            return (
              <div
                key={s}
                onClick={() => setSecteurEdit(s)}
                style={{
                  flex: 1,
                  textAlign: 'center',
                  padding: '9px',
                  borderRadius: 9,
                  fontSize: 10.5,
                  fontWeight: 700,
                  cursor: 'pointer',
                  background: on ? C.navy : C.white,
                  color: on ? '#fff' : C.grey,
                  border: '1px solid ' + C.border,
                }}
              >
                {lb}
              </div>
            );
          })}
        </div>
        <div style={{ fontSize: 8, color: C.muted, marginTop: 7, lineHeight: 1.5 }}>
          Chaque secteur a ses propres coordonnées (nom, adresse, IBAN, logo). Elles apparaissent
          sur les documents du secteur actif.
        </div>
      </div>
    ) : null;
  // Le code PIN ne se garde jamais dans les réglages : il part chiffré sur la
  // fiche de l'employé (`codePinSaisi`), et `soc.membres` s'enregistre en clair.
  const updMembre = (id, patch) =>
    set({
      membres: membresSansCode(
        (soc?.membres || []).map((x) => (x.id === id ? { ...x, ...patch } : x)),
      ),
    });
  const TABS = [
    ['societe', '🏢 Société'],
    ['documents', '📄 Documents'],
    ['banque', '🏦 Banque'],
    ['messages', '✉ Messages'],
    ['envois', '📨 E-mail et envois'],
    ['acces', '🔒 Accès'],
    ['pref', '🌍 Préférences'],
    ['compte', '👤 Mon compte'],
    // La consommation du Pack IA est une question de facture : au patron seul.
    ...(session?.role === 'patron' ? [['packia', '✨ Pack IA']] : []),
  ];
  const Toggle = ({ label, on, onChange, sub }) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '9px 0',
        borderBottom: '1px solid ' + C.border,
      }}
    >
      <div style={{ flex: 1, marginRight: 10 }}>
        <div style={{ fontSize: 10, fontWeight: 600 }}>{label}</div>
        {sub && <div style={{ fontSize: 8, color: C.muted }}>{sub}</div>}
      </div>
      <div
        onClick={() => onChange(!on)}
        style={{
          width: 42,
          height: 24,
          borderRadius: 99,
          background: on ? C.green : C.border,
          position: 'relative',
          cursor: 'pointer',
          flexShrink: 0,
          transition: 'background .15s',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 2,
            left: on ? 20 : 2,
            width: 20,
            height: 20,
            borderRadius: '50%',
            background: '#fff',
            transition: 'left .15s',
          }}
        />
      </div>
    </div>
  );
  return (
    <Shell
      active="plus"
      onNav={onNav}
      soc={soc}
      pageTitle="⚙️ Réglages"
      estMixte={estMixte}
      univers={univers}
      setUnivers={setUnivers}
    >
      <Tabs items={TABS} active={tab} onChange={setTab} />
      <div style={{ marginTop: 12 }} />
      {tab === 'packia' && <PackIaCard onNav={onNav} />}
      {tab === 'societe' && (
        <Card>
          <Bar
            icon="🏢"
            title="Informations société"
            color={C.navy}
            sub={
              estMixte
                ? 'Coordonnées propres à chaque secteur'
                : 'Figurent sur tes devis et factures'
            }
          />
          <div style={{ padding: 13 }}>
            <SectPicker />
            <div style={{ display: 'flex', gap: 13, alignItems: 'center', marginBottom: 14 }}>
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 14,
                  background: coord?.logo ? '#fff' : C.navyXL,
                  border: '1px solid ' + C.border,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  flexShrink: 0,
                }}
              >
                {coord?.logo ? (
                  <img
                    src={coord.logo}
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    alt=""
                  />
                ) : (
                  <span style={{ fontSize: 24, fontWeight: 900, color: C.navy }}>
                    {(coord?.nom || SOC.nom).charAt(0)}
                  </span>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: 'inline-block',
                    background: C.navy,
                    color: '#fff',
                    borderRadius: 9,
                    padding: '8px 12px',
                    fontSize: 10,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  📷 Importer un logo
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const f = e.target.files && e.target.files[0];
                      if (f) {
                        const r = new FileReader();
                        r.onload = () => setCoord({ logo: r.result });
                        r.readAsDataURL(f);
                      }
                    }}
                  />
                </label>
                {coord?.logo && (
                  <div style={{ marginTop: 6 }}>
                    <button
                      onClick={() => setCoord({ logo: null })}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: C.red,
                        fontSize: 9,
                        cursor: 'pointer',
                      }}
                    >
                      Retirer
                    </button>
                  </div>
                )}
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 8, color: C.muted, marginBottom: 3 }}>
                    …ou coller un lien d'image (URL)
                  </div>
                  <input
                    value={(coord?.logo || '').startsWith('http') ? coord.logo : ''}
                    onChange={(e) => setCoord({ logo: e.target.value })}
                    placeholder="https://… (logo.png)"
                    style={{
                      width: '100%',
                      border: '1px solid ' + C.border,
                      borderRadius: 8,
                      padding: '8px 10px',
                      fontSize: 10,
                    }}
                  />
                </div>
              </div>
            </div>
            <div style={{ marginBottom: 13 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: C.grey, marginBottom: 5 }}>
                Forme juridique
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {['Indépendant', 'Sàrl', 'SA', 'Autre'].map((v) => {
                  // Rien de coché tant que la forme n'est pas choisie : une SA
                  // sans forme enregistrée se lisait « Indépendant ».
                  const on = coord?.forme === v;
                  return (
                    <div
                      key={v}
                      onClick={() => setCoord({ forme: v })}
                      style={{
                        flex: '1 1 0',
                        minWidth: 62,
                        textAlign: 'center',
                        padding: '8px 4px',
                        borderRadius: 9,
                        fontSize: 10,
                        fontWeight: 700,
                        cursor: 'pointer',
                        background: on ? C.navy : C.white,
                        color: on ? '#fff' : C.grey,
                        border: '1px solid ' + (on ? C.navy : C.border),
                      }}
                    >
                      {v}
                    </div>
                  );
                })}
              </div>
              {!coord?.forme && (
                <div
                  role="note"
                  style={{ fontSize: 8.5, color: C.amber, fontWeight: 700, marginTop: 5 }}
                >
                  Forme juridique pas encore choisie : SwissTech compte comme pour un indépendant
                  tant qu’elle ne l’est pas.
                </div>
              )}
              <div style={{ fontSize: 8, color: C.muted, marginTop: 5, lineHeight: 1.5 }}>
                Indépendant (raison individuelle) : indique le nom du titulaire, ex. « Jean Exemple
                ». Sàrl / SA : nom de la société, ex. « Exemple Sàrl ».
              </div>
            </div>
            {[
              ["Nom de l'entreprise", 'nom'],
              ['Adresse', 'adr'],
              ['NPA / Ville', 'npa'],
              ['Téléphone', 'tel'],
              ['E-mail', 'email'],
              ['N° TVA', 'tva'],
              ['N° RC', 'rc'],
            ].map(([lb, key]) => (
              <Inp
                key={key}
                label={lb}
                value={coord?.[key] || ''}
                onChange={(e) => setCoord({ [key]: e.target.value })}
              />
            ))}
            {estMixte && (
              <div
                style={{
                  fontSize: 8.5,
                  color: C.muted,
                  marginTop: 7,
                  textAlign: 'center',
                  lineHeight: 1.5,
                }}
              >
                En mode Mix, chaque secteur a ses propres coordonnées (sélecteur en haut de la
                carte).
              </div>
            )}
          </div>
        </Card>
      )}

      {tab === 'societe' && (
        <>
          <SecteurCard soc={soc} onNav={onNav} />
          <UnitesCard soc={soc} set={set} />
          <PointageCard magasin={estMixte ? univers === 'magasin' : soc?.secteur === 'magasin'} />
          <AdressesTravailCard soc={soc} />
          <BanniereCard soc={soc} set={set} />
          <Card style={{ marginBottom: 12 }}>
            <Bar icon="🖥️" title="Affichage" color={C.teal} sub="Adapter l'app à ton écran" />
            <div style={{ padding: 13 }}>
              <div style={{ display: 'flex', gap: 7 }}>
                {[
                  ['auto', '↔️', 'Auto'],
                  ['telephone', '📱', 'Téléphone'],
                  ['tablette', '📲', 'Tablette'],
                  ['ordi', '💻', 'Ordinateur'],
                ].map(([v, ic, lb]) => {
                  const on = (vue || 'auto') === v;
                  return (
                    <div
                      key={v}
                      onClick={() => setVue && setVue(v)}
                      style={{
                        flex: 1,
                        textAlign: 'center',
                        padding: '11px 3px',
                        borderRadius: 11,
                        cursor: 'pointer',
                        background: on ? C.teal : C.navyXL,
                        color: on ? '#fff' : C.ink,
                        border: '1px solid ' + (on ? C.teal : C.border),
                      }}
                    >
                      <div style={{ fontSize: 17 }}>{ic}</div>
                      <div style={{ fontSize: 8.5, marginTop: 3, fontWeight: 800 }}>{lb}</div>
                    </div>
                  );
                })}
              </div>
              <div style={{ fontSize: 8.5, color: C.muted, marginTop: 9, lineHeight: 1.5 }}>
                « Auto » reconnaît l'écran (téléphone, tablette ou ordinateur) et s'y adapte tout
                seul. « Téléphone » garde le format mobile même sur grand écran ; « Tablette » et «
                Ordinateur » forcent leur présentation.
              </div>
            </div>
          </Card>

          <Card style={{ marginBottom: 12 }}>
            <Bar
              icon="🔁"
              title="Factures récurrentes"
              color={C.purple}
              sub="Abonnements, contrats d'entretien, loyers"
            />
            <div style={{ padding: 13 }}>
              <div
                onClick={() => setSoc && setSoc((p) => ({ ...p, recurOn: !p.recurOn }))}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  background: C.navyXL,
                  borderRadius: 11,
                  padding: '11px 13px',
                }}
              >
                <div style={{ flex: 1, marginRight: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: C.ink }}>
                    Activer les factures récurrentes
                  </div>
                  <div style={{ fontSize: 8.5, color: C.muted, marginTop: 2, lineHeight: 1.4 }}>
                    Ajoute l'option sur l'écran d'une facture, pour la regénérer chaque mois /
                    trimestre…
                  </div>
                </div>
                <div
                  style={{
                    width: 46,
                    height: 26,
                    borderRadius: 999,
                    background: soc?.recurOn ? C.purple : C.border,
                    flexShrink: 0,
                    position: 'relative',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: 3,
                      left: soc?.recurOn ? 23 : 3,
                      width: 20,
                      height: 20,
                      borderRadius: 999,
                      background: '#fff',
                      boxShadow: '0 1px 3px rgba(0,0,0,.25)',
                      transition: 'left .15s',
                    }}
                  />
                </div>
              </div>
              <div style={{ fontSize: 8.5, color: C.muted, marginTop: 9, lineHeight: 1.5 }}>
                {avecBatiment ? 'Inutile pour des chantiers ponctuels. ' : ''}Laisse désactivé si
                chaque facture est différente.
              </div>
            </div>
          </Card>

          {(session?.role === 'patron' || session?.role === 'comptable') && <RepriseCard />}
        </>
      )}

      {tab === 'societe' && (
        <div style={{ marginTop: 12 }}>
          <MesSocietesCard />
          <Card style={{ marginBottom: 12 }}>
            <Bar
              icon="🏛"
              title="Charges sociales (taux)"
              color={C.amber}
              sub="Taux employeur et salarié + charges personnalisées"
            />
            <div style={{ padding: 13 }}>
              <EditeurCharges
                soc={soc}
                setSoc={setSoc}
                note="Ces taux s'appliquent à tous les employés. Le LPP (2e pilier) et l'impôt à la source se règlent par employé dans « Équipe » → l'employé."
              />
            </div>
          </Card>
        </div>
      )}

      {tab === 'documents' && (
        <div>
          <Card style={{ marginBottom: 11 }}>
            <Bar
              icon="✍️"
              title="Signatures & tampon"
              color={C.purple}
              sub="Signatures des rôles + tampon de la société"
            />
            <div style={{ padding: '11px 13px' }}>
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 800,
                  color: C.muted,
                  textTransform: 'uppercase',
                  marginBottom: 7,
                }}
              >
                Tampon de la société
              </div>
              {soc?.tampon ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 10 }}>
                  <img
                    src={soc.tampon}
                    alt="tampon"
                    style={{
                      width: 90,
                      height: 90,
                      objectFit: 'contain',
                      border: '1px solid ' + C.border,
                      borderRadius: 8,
                      background: '#fff',
                    }}
                  />
                  <Btn
                    label="Retirer"
                    sm
                    ghost
                    color={C.grey}
                    onClick={() => set({ tampon: '' })}
                  />
                </div>
              ) : null}
              <TamponImport onTampon={(tampon) => set({ tampon })} change={!!soc?.tampon} />
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 800,
                  color: C.muted,
                  textTransform: 'uppercase',
                  margin: '15px 0 7px',
                }}
              >
                Signature de la société (patron)
              </div>
              {soc?.sigPatron ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                  <img
                    src={soc.sigPatron}
                    alt="sig"
                    style={{
                      height: 44,
                      border: '1px solid ' + C.border,
                      borderRadius: 8,
                      background: '#fff',
                    }}
                  />
                  <Btn
                    label="Refaire"
                    sm
                    ghost
                    color={C.grey}
                    onClick={() => set({ sigPatron: '' })}
                  />
                </div>
              ) : (
                <SignaturePad aValider onSign={(data) => set({ sigPatron: data })} />
              )}
              {(soc?.membres || []).length > 0 && (
                <div>
                  <div
                    style={{
                      fontSize: 9,
                      fontWeight: 800,
                      color: C.muted,
                      textTransform: 'uppercase',
                      margin: '15px 0 4px',
                    }}
                  >
                    Signatures des collaborateurs
                  </div>
                  {(soc?.membres || []).map((m) => (
                    <div
                      key={m.id}
                      style={{
                        borderTop: '1px solid ' + C.border,
                        paddingTop: 9,
                        marginTop: 9,
                      }}
                    >
                      <div style={{ fontSize: 10.5, fontWeight: 800, marginBottom: 6 }}>
                        {m.nom || m.prenom || 'Membre'}{' '}
                        <span style={{ fontWeight: 600, color: C.muted, fontSize: 8.5 }}>
                          · {m.role || ''}
                        </span>
                      </div>
                      {m.sig ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                          <img
                            src={m.sig}
                            alt=""
                            style={{
                              height: 40,
                              border: '1px solid ' + C.border,
                              borderRadius: 8,
                              background: '#fff',
                            }}
                          />
                          <Btn
                            label="Refaire"
                            sm
                            ghost
                            color={C.grey}
                            onClick={() => updMembre(m.id, { sig: '' })}
                          />
                        </div>
                      ) : (
                        <SignaturePad aValider onSign={(data) => updMembre(m.id, { sig: data })} />
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 800,
                  color: C.muted,
                  textTransform: 'uppercase',
                  margin: '14px 0 4px',
                }}
              >
                Mettre le tampon + ma signature sur
              </div>
              {SORTES_TAMPON.map(([cle, libelle]) => {
                const coche = tamponVoulu(soc, cle);
                return (
                  <label
                    key={cle}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 9,
                      padding: '7px 0',
                      borderBottom: '1px solid ' + C.border,
                      fontSize: 11,
                      color: C.ink,
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={coche}
                      onChange={() =>
                        set({ tamponSur: { ...(soc?.tamponSur || {}), [cle]: !coche } })
                      }
                      style={{ width: 17, height: 17, accentColor: C.teal }}
                    />
                    {libelle}
                  </label>
                );
              })}
              <div style={{ fontSize: 8, color: C.muted, marginTop: 9, lineHeight: 1.5 }}>
                Coché : le tampon et ta signature se mettent d’office sur ces documents. Sur une
                offre ou une facture, tu peux encore les enlever avec le bouton « Apposer le tampon
                + ma signature ».
              </div>
            </div>
          </Card>
          <Card style={{ marginBottom: 11 }}>
            <Bar
              icon="🎨"
              title="Modèle des documents"
              color={DA}
              sub="Aperçu réel — touche un modèle pour voir la vraie page"
            />
            <div style={{ padding: 13 }}>
              <DefautsDocuments soc={soc} set={set} />
              {(() => {
                const estMag0 = estMixte ? univers === 'magasin' : soc?.secteur === 'magasin';
                const { client: prevClient, doc: prevDoc } = offreDExemple(
                  soc,
                  estMag0,
                  soc?.devisStyle || 'suisse',
                  soc?.docCouleur,
                );
                return (
                  <div
                    style={{
                      overflow: 'hidden',
                      borderRadius: 12,
                      border: '1px solid ' + C.border,
                      height: 432,
                      background: '#e5e7eb',
                      marginBottom: 12,
                    }}
                  >
                    <div
                      style={{
                        transform: 'scale(0.5)',
                        transformOrigin: 'top left',
                        width: '200%',
                      }}
                    >
                      <DocumentA4
                        preview
                        soc={soc}
                        client={prevClient}
                        doc={prevDoc}
                        onClose={() => {}}
                      />
                    </div>
                  </div>
                );
              })()}
              <LogoDuDocument coord={coord} setCoord={setCoord} />
              {(() => {
                const cur =
                  DEVIS_STYLES.find((x) => x.id === (soc?.devisStyle || 'suisse')) ||
                  DEVIS_STYLES[0];
                return (
                  <button
                    type="button"
                    onClick={() => setStyleOuvert(true)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: '1.5px solid ' + DA,
                      background: C.white,
                      cursor: 'pointer',
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        flexShrink: 0,
                        background: accentDuDocument(soc?.docCouleur, cur.id),
                        border: '2px solid #fff',
                        boxShadow: '0 0 0 1px ' + C.border,
                      }}
                    />
                    <span style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
                      <span
                        style={{ display: 'block', fontSize: 12, fontWeight: 800, color: C.ink }}
                      >
                        {cur.nom}
                      </span>
                      <span style={{ display: 'block', fontSize: 9, color: C.muted }}>
                        {tr('Couleur des documents')}
                      </span>
                    </span>
                    <span style={{ fontSize: 11, fontWeight: 800, color: DA }}>
                      {tr('Modifier')} ›
                    </span>
                  </button>
                );
              })()}
              {styleOuvert && (
                <StyleDesDocuments
                  soc={soc}
                  estMag={estMixte ? univers === 'magasin' : soc?.secteur === 'magasin'}
                  set={set}
                  onClose={() => setStyleOuvert(false)}
                />
              )}
              <div style={{ fontSize: 8, color: C.muted, marginTop: 10 }}>
                L'aperçu ci-dessus utilise tes coordonnées enregistrées. Ce modèle et la couleur
                s'appliquent à tous tes devis et factures.
              </div>
            </div>
          </Card>
          <Card style={{ marginBottom: 11 }}>
            <Bar
              icon="📝"
              title="Nom du document (offre)"
              color={C.navy}
              sub="« Offre » ou « Devis » : le mot vaut partout — écran, PDF, e-mail, page du client. Auto : Offre au magasin et en Suisse, Devis ailleurs."
            />
            <div style={{ padding: 13, display: 'flex', gap: 6 }}>
              {[
                ['', 'Auto'],
                ['offre', 'Offre'],
                ['devis', 'Devis'],
              ].map(([v, lb]) => {
                const on = (soc?.motOffre || '') === v;
                return (
                  <div
                    key={lb}
                    onClick={() => set({ motOffre: v })}
                    style={{
                      flex: 1,
                      textAlign: 'center',
                      cursor: 'pointer',
                      border: '1px solid ' + (on ? C.ink : C.border),
                      borderRadius: 9,
                      padding: '10px',
                      fontSize: 11,
                      fontWeight: 800,
                      background: on ? C.navy : '#fff',
                      color: on ? '#fff' : C.grey,
                    }}
                  >
                    {lb}
                  </div>
                );
              })}
            </div>
          </Card>
          <Card style={{ marginBottom: 11 }}>
            <Bar
              icon="🔤"
              title="Écriture des documents"
              color={C.navy}
              sub="Police et taille appliquées à tous tes devis & factures"
            />
            <div style={{ padding: 13 }}>
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 800,
                  color: C.muted,
                  textTransform: 'uppercase',
                  marginBottom: 7,
                }}
              >
                Police
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 13 }}>
                {CHOIX_POLICES.map(([v, lb]) => {
                  const on = (soc?.docFont || '') === v;
                  return (
                    <div
                      key={lb}
                      onClick={() => set({ docFont: v })}
                      style={{
                        cursor: 'pointer',
                        border: '1px solid ' + (on ? C.ink : C.border),
                        borderRadius: 9,
                        padding: '8px 12px',
                        fontSize: 11.5,
                        fontWeight: 700,
                        fontFamily: pileDePolice(v) || 'inherit',
                        background: on ? C.navyXL : C.white,
                        color: C.ink,
                      }}
                    >
                      {lb}
                      {on ? ' ✓' : ''}
                    </div>
                  );
                })}
              </div>
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 800,
                  color: C.muted,
                  textTransform: 'uppercase',
                  marginBottom: 7,
                }}
              >
                Taille
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {[
                  [0.92, 'Compact'],
                  [1, 'Normal'],
                  [1.08, 'Grand'],
                ].map(([v, lb]) => {
                  const on = (soc?.docSize || 1) === v;
                  return (
                    <div
                      key={lb}
                      onClick={() => set({ docSize: v })}
                      style={{
                        flex: 1,
                        textAlign: 'center',
                        cursor: 'pointer',
                        border: '1px solid ' + (on ? C.ink : C.border),
                        borderRadius: 9,
                        padding: '10px',
                        fontSize: 10.5,
                        fontWeight: 800,
                        background: on ? C.navy : C.white,
                        color: on ? '#fff' : C.grey,
                      }}
                    >
                      {lb}
                    </div>
                  );
                })}
              </div>
              <div style={{ fontSize: 8.5, color: C.muted, marginTop: 9, lineHeight: 1.5 }}>
                S'applique à tous tes devis, factures et bons de commande. La QR-facture garde sa
                taille officielle.
              </div>
            </div>
          </Card>
          <Card style={{ marginBottom: 11 }}>
            <Bar icon="📍" title="Position du logo" color={C.navy} />
            <div style={{ padding: 13, display: 'flex', gap: 6 }}>
              {[
                ['gauche', 'Gauche'],
                ['milieu', 'Centré'],
                ['droite', 'Droite'],
              ].map(([v, lb]) => {
                const on = (soc?.logoPos || 'droite') === v;
                return (
                  <div
                    key={v}
                    onClick={() => set({ logoPos: v })}
                    style={{
                      flex: 1,
                      textAlign: 'center',
                      padding: '9px',
                      borderRadius: 9,
                      fontSize: 10,
                      fontWeight: 700,
                      cursor: 'pointer',
                      background: on ? C.navy : C.white,
                      color: on ? '#fff' : C.grey,
                      border: '1px solid ' + C.border,
                    }}
                  >
                    {lb}
                  </div>
                );
              })}
            </div>
          </Card>
          <TauxTvaCard soc={soc} set={set} />
          <Card style={{ marginBottom: 11 }}>
            <Bar
              icon="💧"
              title="Logo en filigrane (fond du document)"
              color={C.teal}
              sub="Modèle « Filigrane logo » — déplace-le librement"
            />
            <div style={{ padding: 13 }}>
              <FiligraneControls soc={soc} setSoc={setSoc} />
            </div>
          </Card>
          <Card style={{ marginBottom: 11 }}>
            <Bar
              icon="🌐"
              title="Site web & réseaux sociaux"
              color={C.teal}
              sub="Coche ceux à afficher (cliquables) sur les documents"
            />
            <div style={{ padding: 13 }}>
              {RESEAUX.map((r) => {
                const R = soc?.reseaux || {};
                const S = soc?.reseauxShow || {};
                const val = R[r.key] || '';
                const shown = val && S[r.key] !== false;
                return (
                  <div
                    key={r.key}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}
                  >
                    <span style={{ fontSize: 15, width: 20, textAlign: 'center' }}>{r.icon}</span>
                    <span
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        color: C.grey,
                        width: 62,
                        flexShrink: 0,
                      }}
                    >
                      {r.nom}
                    </span>
                    <input
                      value={val}
                      onChange={(e) => set({ reseaux: { ...R, [r.key]: e.target.value } })}
                      placeholder={
                        r.key === 'site'
                          ? 'www.monentreprise.ch'
                          : r.key === 'taplink'
                            ? 'taplink.cc/monentreprise'
                            : r.key === 'whatsapp'
                              ? '+41 79 …'
                              : '@' + r.nom.toLowerCase()
                      }
                      style={{ ..._inp, flex: 1 }}
                    />
                    <div
                      onClick={() => {
                        if (!val) return;
                        set({ reseauxShow: { ...S, [r.key]: !(S[r.key] !== false) } });
                      }}
                      style={{
                        flexShrink: 0,
                        width: 46,
                        textAlign: 'center',
                        padding: '7px 0',
                        borderRadius: 8,
                        fontSize: 8.5,
                        fontWeight: 800,
                        cursor: val ? 'pointer' : 'default',
                        opacity: val ? 1 : 0.4,
                        background: shown ? C.teal : C.white,
                        color: shown ? '#fff' : C.grey,
                        border: '1px solid ' + (shown ? C.teal : C.border),
                      }}
                    >
                      {shown ? 'Affiché' : 'Masqué'}
                    </div>
                  </div>
                );
              })}
              <button
                onClick={() => {
                  setResOk(true);
                  setTimeout(() => setResOk(false), 2200);
                }}
                style={{
                  width: '100%',
                  marginTop: 8,
                  background: resOk ? C.green : C.teal,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  padding: '12px',
                  fontSize: 12,
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                {resOk ? '✓ Réseaux enregistrés !' : '✓ Enregistrer les réseaux'}
              </button>
              <div style={{ fontSize: 8, color: C.muted, marginTop: 7 }}>
                Saisis un identifiant (@nom) ou un lien complet. Sur le devis/la facture, chaque
                réseau affiché devient un lien cliquable vers Instagram, Facebook, TapLink, etc.
                Tout est enregistré automatiquement ; ce bouton ne fait que le confirmer.
              </div>
            </div>
          </Card>
          <Card>
            <Bar icon="👁" title="Éléments affichés" color={C.navy} />
            <div style={{ padding: '4px 13px 13px' }}>
              <Toggle
                label="QR-facture suisse"
                sub="Bulletin de versement scannable"
                on={soc?.showQr !== false}
                onChange={(v) => set({ showQr: v })}
              />
              <Toggle
                label="Coordonnées bancaires (si pas de QR)"
                on={soc?.showIban !== false}
                onChange={(v) => set({ showIban: v })}
              />
              <Toggle
                label="Mentions légales (CO / SIA)"
                on={soc?.showMentions !== false}
                onChange={(v) => set({ showMentions: v })}
              />
              {/* Le modèle de document par défaut ne les porte pas : ils s'y
                  ajoutent quand ils sont cochés. */}
              <Toggle
                label="Téléphone société"
                on={AF.socTel === true}
                onChange={(v) => setAF('socTel', v)}
              />
              <Toggle
                label="E-mail société"
                on={AF.socEmail === true}
                onChange={(v) => setAF('socEmail', v)}
              />
              <Toggle
                label="N° TVA sur le document"
                on={AF.socTva !== false}
                onChange={(v) => setAF('socTva', v)}
              />
              <Toggle
                label="Garantie (art. 371 CO)"
                on={AF.garantie !== false}
                onChange={(v) => setAF('garantie', v)}
              />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingTop: 10,
                }}
              >
                <span style={{ fontSize: 10, fontWeight: 600 }}>Durée de garantie (ans)</span>
                <NumInput
                  value={soc?.garantieAns ?? 2}
                  onChange={(e) => set({ garantieAns: +e.target.value })}
                  style={{
                    width: 60,
                    border: '1px solid ' + C.border,
                    borderRadius: 8,
                    padding: '6px',
                    fontSize: 11,
                    textAlign: 'center',
                  }}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingTop: 10,
                }}
              >
                <span style={{ fontSize: 10, fontWeight: 600 }}>Acompte payable sous (jours)</span>
                <NumInput
                  value={soc?.acompteDelai ?? 10}
                  onChange={(e) => set({ acompteDelai: +e.target.value })}
                  style={{
                    width: 60,
                    border: '1px solid ' + C.border,
                    borderRadius: 8,
                    padding: '6px',
                    fontSize: 11,
                    textAlign: 'center',
                  }}
                />
              </div>
            </div>
          </Card>
        </div>
      )}

      {tab === 'banque' && (
        <div>
          <Card style={{ marginBottom: 11 }}>
            <Bar
              icon="🧾"
              title="Sur le document, afficher"
              color={C.navy}
              sub="Comment le client paie ta facture"
            />
            <div style={{ padding: 13 }}>
              <input
                ref={champBulletin}
                type="file"
                accept="application/pdf,image/*"
                aria-label="Bulletin de ma banque"
                style={{ display: 'none' }}
                onChange={joinsBulletin}
              />
              <div style={{ display: 'flex', gap: 6 }}>
                {(systemePays
                  ? [
                      ['deux', 'QR code + IBAN', systemePays === 'SUISSE' ? '🇨🇭' : '📱'],
                      ['iban', 'IBAN uniquement', '🏦'],
                      ['bulletin', 'Bulletin de ma banque', '📎'],
                    ]
                  : [
                      ['iban', 'IBAN uniquement', '🏦'],
                      ['bulletin', 'Bulletin de ma banque', '📎'],
                    ]
                ).map(([v, lb, ic]) => {
                  const on = modePaiement === v;
                  return (
                    <div
                      key={v}
                      onClick={() => {
                        set({ paiement: v, showQr: v === 'deux', showIban: true });
                        // Toucher « Bulletin de ma banque » ouvre tout de suite le choix du fichier.
                        if (v === 'bulletin') champBulletin.current?.click();
                      }}
                      style={{
                        flex: 1,
                        cursor: 'pointer',
                        border: '2px solid ' + (on ? C.navy : C.border),
                        borderRadius: 11,
                        padding: '10px 9px',
                        background: on ? C.navyXL : C.white,
                        textAlign: 'center',
                      }}
                    >
                      <div style={{ fontSize: 18 }}>{ic}</div>
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 800,
                          marginTop: 3,
                          color: on ? C.navy : C.grey,
                        }}
                      >
                        {lb}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ fontSize: 8, color: C.muted, marginTop: 8 }}>
                {modePaiement === 'bulletin'
                  ? 'Joins le bulletin de versement que ta banque t’a remis (PDF ou photo). SwissTech lit son QR code et refait ce bulletin au bas de chaque offre et facture, rempli automatiquement : le client, le montant et la référence.'
                  : !systemePays
                    ? 'Ce pays n’a pas de système de QR code bancaire : la facture porte l’IBAN et les coordonnées bancaires.'
                    : modePaiement === 'iban'
                      ? 'IBAN uniquement : pas de QR code, le client fait le virement avec les coordonnées bancaires.'
                      : infosQr.depuis === 'iban'
                        ? (systemePays === 'SUISSE' ? 'QR-facture' : 'QR-code européen (SEPA)') +
                          ' calculé automatiquement depuis ton IBAN, avec le montant et la référence de chaque facture, et ton IBAN affiché.'
                        : infosQr.depuis === 'identifiant'
                          ? 'QR code ' +
                            infosQr.nom +
                            ' calculé automatiquement depuis ton identifiant ci-dessous, avec le montant de chaque facture, et ton IBAN affiché.'
                          : 'Dans ce pays, seule ta banque peut créer le QR code : ajoute-le ci-dessous, il figure sur chaque facture avec ton IBAN.'}
              </div>
              {systemePays && modePaiement === 'deux' && infosQr.depuis === 'identifiant' && (
                <div style={{ marginTop: 10 }}>
                  <Inp
                    label={infosQr.champ}
                    value={soc?.qrId || ''}
                    onChange={(e) => set({ qrId: e.target.value })}
                    placeholder={infosQr.exemple}
                  />
                  {infosQr.champBanque && (
                    <Inp
                      label={infosQr.champBanque}
                      value={soc?.qrBanque || ''}
                      onChange={(e) => set({ qrBanque: e.target.value })}
                      placeholder={infosQr.exempleBanque}
                    />
                  )}
                  {!String(soc?.qrId || '').trim() && (
                    <div style={{ fontSize: 8.5, color: C.amber, fontWeight: 700 }}>
                      ⚠ Sans cet identifiant, la facture porte l’IBAN seul.
                    </div>
                  )}
                </div>
              )}
              {systemePays && modePaiement === 'deux' && infosQr.depuis === 'image' && (
                <div
                  style={{
                    marginTop: 10,
                    display: 'flex',
                    gap: 12,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  {soc?.qrImage ? (
                    <img
                      src={soc.qrImage}
                      alt="QR code de la banque"
                      style={{
                        width: 96,
                        height: 96,
                        objectFit: 'contain',
                        border: '1px solid ' + C.border,
                        borderRadius: 8,
                        background: '#fff',
                      }}
                    />
                  ) : null}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label
                      style={{
                        cursor: 'pointer',
                        fontSize: 10,
                        fontWeight: 800,
                        color: C.navy,
                        border: '2px solid ' + C.navy,
                        borderRadius: 10,
                        padding: '8px 12px',
                        textAlign: 'center',
                      }}
                    >
                      {soc?.qrImage
                        ? '🔄 Remplacer le QR code'
                        : '📷 Ajouter le QR code de ma banque'}
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={async (e) => {
                          const f = e.target.files && e.target.files[0];
                          e.target.value = '';
                          if (!f) return;
                          try {
                            set({ qrImage: await reduisImageQr(f) });
                            setErreurQr('');
                          } catch (err) {
                            setErreurQr(
                              'Cette image ne peut pas être lue. Essaie une capture d’écran du QR code.',
                            );
                          }
                        }}
                      />
                    </label>
                    {soc?.qrImage && (
                      <button
                        type="button"
                        onClick={() => set({ qrImage: '' })}
                        style={{
                          fontSize: 9,
                          color: C.red,
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                      >
                        Retirer le QR code
                      </button>
                    )}
                  </div>
                  <div style={{ fontSize: 8.5, color: C.muted, flex: '1 1 160px' }}>
                    Ta banque te donne ce QR code (dans son application ou sur demande). Le client
                    le scanne puis saisit le montant de la facture.
                  </div>
                  {erreurQr && (
                    <div style={{ fontSize: 8.5, color: C.red, fontWeight: 700, width: '100%' }}>
                      {erreurQr}
                    </div>
                  )}
                </div>
              )}
              {modePaiement === 'bulletin' && (
                <div style={{ marginTop: 10 }}>
                  {soc?.bulletinImage ? (
                    <img
                      src={soc.bulletinImage}
                      alt="Bulletin de ma banque"
                      style={{
                        width: '100%',
                        display: 'block',
                        border: '1px solid ' + C.border,
                        borderRadius: 8,
                        background: '#fff',
                        marginBottom: 8,
                      }}
                    />
                  ) : null}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <label
                      style={{
                        cursor: 'pointer',
                        fontSize: 10,
                        fontWeight: 800,
                        color: C.navy,
                        border: '2px solid ' + C.navy,
                        borderRadius: 10,
                        padding: '8px 12px',
                        textAlign: 'center',
                      }}
                    >
                      {lectureBulletin
                        ? '⏳ Lecture…'
                        : soc?.bulletinImage
                          ? '🔄 Remplacer le bulletin'
                          : '📎 Joindre le bulletin de ma banque (PDF ou photo)'}
                      <input
                        type="file"
                        accept="application/pdf,image/*"
                        style={{ display: 'none' }}
                        disabled={lectureBulletin}
                        onChange={joinsBulletin}
                      />
                    </label>
                    {soc?.bulletinImage && (
                      <button
                        type="button"
                        onClick={() => set({ bulletinImage: '', bulletinLu: null })}
                        style={{
                          fontSize: 9,
                          color: C.red,
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                        }}
                      >
                        Retirer le bulletin
                      </button>
                    )}
                  </div>
                  {soc?.bulletinImage && soc?.bulletinLu && (
                    <div style={{ fontSize: 9, color: C.teal, fontWeight: 700, marginTop: 6 }}>
                      {'✓ Bulletin lu : ' +
                        [
                          soc.bulletinLu.nom,
                          String(soc.bulletinLu.iban || '')
                            .replace(/(.{4})/g, '$1 ')
                            .trim(),
                        ]
                          .filter(Boolean)
                          .join(' · ') +
                        '. Chaque offre et facture refait ce bulletin avec le client, le montant et la référence.'}
                    </div>
                  )}
                  {soc?.bulletinImage && !soc?.bulletinLu && (
                    <div style={{ fontSize: 8.5, color: C.amber, fontWeight: 700, marginTop: 6 }}>
                      ⚠ Le QR code de ce bulletin n’a pas pu être lu : il est imprimé tel quel, sans
                      client ni montant. Essaie le PDF de la banque ou une photo plus nette.
                    </div>
                  )}
                  {!soc?.bulletinImage && (
                    <div style={{ fontSize: 8.5, color: C.amber, fontWeight: 700, marginTop: 6 }}>
                      ⚠ Sans bulletin joint, la facture porte l’IBAN seul.
                    </div>
                  )}
                  {erreurBulletin && (
                    <div style={{ fontSize: 8.5, color: C.red, fontWeight: 700, marginTop: 6 }}>
                      {erreurBulletin}
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
          <Card style={{ marginBottom: 11 }}>
            <Bar
              icon="🏦"
              title="Coordonnées bancaires"
              color={C.navy}
              sub="IBAN et banque, affichés sur chaque facture"
            />
            <div style={{ padding: 13 }}>
              <SectPicker />
              <Inp
                label="IBAN"
                value={coord?.iban || ''}
                onChange={(e) => setCoord({ iban: e.target.value })}
                placeholder="CH93 0076 2011 6238 5295 7"
              />
              <Inp
                label="Banque"
                value={coord?.banque || ''}
                onChange={(e) => setCoord({ banque: e.target.value })}
                placeholder="PostFinance / UBS / …"
              />
              <Inp
                label="BIC / SWIFT (zone euro / SEPA)"
                value={coord?.bic || ''}
                onChange={(e) => setCoord({ bic: e.target.value })}
                placeholder="ex : AGRIFRPP"
              />
              <Inp
                label="Message d'envoi (e-mail / WhatsApp)"
                rows={4}
                value={soc?.msgEnvoi || ''}
                onChange={(e) => set({ msgEnvoi: e.target.value })}
                placeholder={
                  "Bonjour {client},\n\nVeuillez trouver notre {doc} N\u00b0 {num} d'un montant de {montant}.\n\nCordialement,\n{societe}"
                }
              />
              <div style={{ fontSize: 8, color: C.muted, marginTop: 4 }}>
                Variables : {'{client}'}, {'{doc}'} (devis/facture), {'{num}'}, {'{montant}'},{' '}
                {'{societe}'}. Laisse vide pour le texte par défaut.
              </div>
              <div
                style={{
                  background: C.tealL,
                  borderRadius: 9,
                  padding: '9px 11px',
                  fontSize: 8.5,
                  color: C.grey,
                }}
              >
                Suisse : un IBAN (ou QR-IBAN) génère un QR-facture conforme à la norme SIX. Zone
                euro : IBAN + BIC génèrent un QR SEPA (EPC) scannable par les apps bancaires
                européennes. Ailleurs, les coordonnées IBAN sont affichées.
              </div>
              {(coord?.iban || '').replace(/\s/g, '').length >= 15 && (
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 8.5,
                    fontWeight: 700,
                    color: isQRIban(coord?.iban) ? C.green : C.navy,
                  }}
                >
                  {isQRIban(coord?.iban)
                    ? '✓ QR-IBAN détecté — référence QRR (rapprochement automatique des paiements).'
                    : 'ℹ IBAN normal détecté — référence SCOR (ISO 11649) utilisée automatiquement.'}
                </div>
              )}
              {(coord?.iban || '').replace(/\s/g, '').length >= 15 && (
                <div
                  style={{
                    marginTop: 12,
                    display: 'flex',
                    gap: 12,
                    alignItems: 'center',
                    background: C.navyXL,
                    borderRadius: 10,
                    padding: 12,
                  }}
                >
                  <div
                    style={{
                      position: 'relative',
                      width: 90,
                      height: 90,
                      background: '#fff',
                      borderRadius: 6,
                      padding: 4,
                    }}
                  >
                    <QRCode
                      size={82}
                      color={C.ink}
                      value={swissQR({
                        iban: coord?.iban || '',
                        nom: coord?.nom || SOC.nom,
                        adr: coord?.adr || SOC.adr,
                        npa: coord?.npa || SOC.npa,
                        amount: 100,
                        clientNom: 'Client exemple',
                        clientAdr: '',
                        clientNpa: '',
                        refType: docRef('APERCU', coord?.iban).type,
                        ref: docRef('APERCU', coord?.iban).ref,
                        msg: '',
                      })}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%,-50%)',
                        background: '#fff',
                        width: 16,
                        height: 16,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 11,
                      }}
                    >
                      ✛
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 9.5, fontWeight: 800, color: C.navy }}>
                      Aperçu QR-facture 🇨🇭
                      {estMixte ? ' · ' + (secteurEdit === 'magasin' ? 'Magasin' : 'Bâtiment') : ''}
                    </div>
                    <div style={{ fontSize: 8, color: C.muted, marginTop: 3, lineHeight: 1.5 }}>
                      Généré depuis l'IBAN de ce secteur.
                      <br />
                      Ce code apparaît sur les factures du secteur.
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Card>
          <Card style={{ marginTop: 11 }}>
            <Bar
              icon="🧾"
              title="Méthode de décompte TVA"
              color={C.teal}
              sub="Selon ton annonce à l'AFC"
            />
            <div style={{ padding: 13 }}>
              {/*
                L'AFC admet deux methodes. Le decompte de l'application etait
                fige sur celle de l'encaissement, alors que la plupart des
                entreprises decomptent a la facturation : leur decompte
                restait a zero tant qu'un client n'avait pas paye.
              */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 6 }}>
                {Object.entries(METHODES_TVA).map(([cle, libelle]) => {
                  const choisi = (soc?.tvaMethode || 'convenues') === cle;
                  return (
                    <div
                      key={cle}
                      onClick={() => set({ tvaMethode: cle })}
                      style={{
                        border: '1px solid ' + (choisi ? C.teal : C.border),
                        background: choisi ? C.tealL : C.white,
                        borderRadius: 10,
                        padding: '10px 12px',
                        fontSize: 10,
                        fontWeight: choisi ? 800 : 600,
                        color: choisi ? C.teal : C.grey,
                        cursor: 'pointer',
                      }}
                    >
                      {libelle}
                    </div>
                  );
                })}
              </div>
              <div style={{ fontSize: 9, color: C.muted, lineHeight: 1.5 }}>
                « Convenues » est le régime ordinaire, celui que l'on reçoit en s'annonçant à l'AFC
                : la facture compte dès son émission. « Reçues » ne retient que ce qui est encaissé,
                et doit être demandé à l'AFC.
              </div>
            </div>
          </Card>
        </div>
      )}

      {tab === 'messages' && (
        <Card>
          <Bar
            icon="✉"
            title="Modèles de messages"
            color={C.navy}
            sub={tr('Variables :') + ' {client} {num} {lien} {montant}'}
          />
          <div style={{ padding: 13 }}>
            <Inp
              label="Message d'envoi de devis"
              rows={5}
              value={soc?.tplDevis ?? TPL_DEVIS_DEF}
              onChange={(e) => set({ tplDevis: e.target.value })}
            />
            <Inp
              label="Message d'envoi de facture"
              rows={5}
              value={soc?.tplFacture ?? TPL_FACTURE_DEF}
              onChange={(e) => set({ tplFacture: e.target.value })}
            />
            <Btn
              label="↺ Réinitialiser"
              sm
              ghost
              color={C.grey}
              onClick={() => set({ tplDevis: TPL_DEVIS_DEF, tplFacture: TPL_FACTURE_DEF })}
            />
          </div>
        </Card>
      )}

      {tab === 'compte' && <MonCompteCard soc={soc} />}
      {tab === 'compte' && <MaSignatureCard />}
      {tab === 'compte' && <ComptesEquipeCard onNav={onNav} />}
      {tab === 'compte' && <RepartirAZeroCard />}
      {tab === 'compte' && <AccesCreateurCard onNav={onNav} />}
      {tab === 'envois' && <BoiteEnvoiCard soc={soc} />}
      {tab === 'envois' && <EnvoisAutoCard />}
      {tab === 'envois' && <TextesRelancesCard soc={soc} />}
      {tab === 'envois' && (
        <div style={{ marginTop: 12 }}>
          <Card style={{ marginBottom: 12 }}>
            <Bar
              icon="🔔"
              title="Rappels de paiement"
              color={C.red}
              sub="Manuel par défaut · l'automatique arrivera avec le lien bancaire"
            />
            <div style={{ padding: 13 }}>
              <div
                onClick={() =>
                  setSoc && setSoc((p) => ({ ...p, rappelAuto: !(p.rappelAuto === true) }))
                }
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  background: C.navyXL,
                  borderRadius: 11,
                  padding: '11px 13px',
                }}
              >
                <div style={{ flex: 1, marginRight: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: C.ink }}>
                    Rappels automatiques
                  </div>
                  <div style={{ fontSize: 8.5, color: C.muted, marginTop: 2, lineHeight: 1.4 }}>
                    Désactivé (recommandé) : tu envoies tes rappels quand tu veux. Activé : le 1er
                    rappel se prépare seul dès l&apos;échéance dépassée.
                  </div>
                </div>
                <div
                  style={{
                    width: 46,
                    height: 26,
                    borderRadius: 999,
                    background: soc?.rappelAuto === true ? C.red : C.border,
                    flexShrink: 0,
                    position: 'relative',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: 3,
                      left: soc?.rappelAuto === true ? 23 : 3,
                      width: 20,
                      height: 20,
                      borderRadius: 999,
                      background: '#fff',
                      boxShadow: '0 1px 3px rgba(0,0,0,.25)',
                      transition: 'left .15s',
                    }}
                  />
                </div>
              </div>
              <div style={{ fontSize: 8.5, color: C.muted, marginTop: 9, lineHeight: 1.5 }}>
                ⚠️ Le rappel vraiment automatique nécessitera le lien bancaire pour savoir qui a
                déjà payé et ne relancer personne à tort. En attendant, garde le mode manuel : tu
                gardes la main sur chaque envoi.
              </div>
            </div>
          </Card>
        </div>
      )}

      {tab === 'acces' &&
        (() => {
          const membres = soc?.membres || [];
          const ROLES_ASSIGN = [
            ['technicien', '⚙', "Technicien / Chef d'équipe"],
            ['comptable', '∑', 'Comptable'],
            ['secretaire', '✎', 'Secrétaire'],
            ['ouvrier', '⚒', 'Employé'],
          ];
          const addMembre = (m) =>
            set({ membres: membresSansCode([...(soc?.membres || []), { id: uid(), ...m }]) });
          // Le code lui-même n'est jamais affiché : seul compte qu'il soit
          // défini sur la fiche liée.
          const codePinDuMembre = (m) => {
            const e = (employes || []).find((x) => String(x.id) === String(m.empId));
            return !!e && (e.codePinDefini === true || /^\d{4}$/.test(String(e.codePin ?? '')));
          };
          const delMembre = (id) =>
            set({ membres: membresSansCode((soc?.membres || []).filter((x) => x.id !== id)) });
          const A = soc?.acces || {};
          const getA = (role, key, def) => {
            const r = A[role] || {};
            return r[key] !== undefined ? r[key] : def;
          };
          const setA = (role, key, val) =>
            set({ acces: { ...A, [role]: { ...(A[role] || {}), [key]: val } } });
          return (
            <div>
              <Card style={{ marginBottom: 11 }}>
                <Bar
                  icon="🔐"
                  title="Rôles & accès"
                  color={C.navy}
                  sub="Choisis ce que chaque rôle voit — réglable pour ta société"
                />
                <div style={{ padding: '11px 13px 8px' }}>
                  <div style={{ fontSize: 9, color: C.muted, lineHeight: 1.5 }}>
                    Décoche pour masquer une page à un rôle. La <b>comptabilité</b> reste réservée
                    au patron et au comptable ; l'accès <b>stock / catalogue</b> se règle dans la
                    carte plus bas. Ces réglages valent aussi pour le serveur : une page fermée
                    n'est plus servie par l'API, un salaire ou un prix d'achat masqué n'en sort pas.
                  </div>
                </div>
              </Card>
              {[
                ['technicien', '⚙', "Technicien / Chef d'équipe", C.teal],
                ['directeur', '🧭', 'Directeur des travaux', C.navy],
                ['secretaire', '✎', 'Secrétaire', C.purple],
              ].map(([role, ic, lb, col]) => (
                <Card key={role} style={{ marginBottom: 11 }}>
                  <Bar icon={ic} title={lb} color={col} />
                  <div style={{ padding: '4px 13px 12px' }}>
                    <div
                      style={{
                        fontSize: 9,
                        fontWeight: 800,
                        color: C.muted,
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                        margin: '7px 0 1px',
                      }}
                    >
                      Pages
                    </div>
                    {[
                      ['offres', avecBatiment ? 'Offres / Devis' : 'Offres'],
                      [
                        'prixOffres',
                        tr('Prix des offres (sinon : travaux et quantités seulement)'),
                      ],
                      ['factures', 'Factures client'],
                      ...(avecBatiment ? [['chantiers', 'Chantiers']] : []),
                      ['clients', 'Clients'],
                      ['rdv', avecBatiment ? 'RDV / Planning' : 'Rendez-vous'],
                      ['equipe', 'Équipe'],
                      ['depenses', 'Dépenses / frais'],
                    ].map(([k, l]) => (
                      <Toggle
                        key={k}
                        label={l}
                        on={getA(role, k, true)}
                        onChange={(v) => setA(role, k, v)}
                      />
                    ))}
                    {/* Les droits sur un chantier sont du bâtiment. */}
                    {avecBatiment && (
                      <>
                        <div
                          style={{
                            fontSize: 9,
                            fontWeight: 800,
                            color: C.muted,
                            textTransform: 'uppercase',
                            letterSpacing: 0.4,
                            margin: '11px 0 1px',
                          }}
                        >
                          Sur un chantier
                        </div>
                        {[
                          ['chHeures', 'Heures (pointage)'],
                          ['chSous', 'Sous-traitants'],
                          ['chFacf', 'Factures fournisseurs / matériel'],
                          ['chPhotos', 'Photos / Plans / Rapports'],
                        ].map(([k, l]) => (
                          <Toggle
                            key={k}
                            label={l}
                            on={getA(role, k, true)}
                            onChange={(v) => setA(role, k, v)}
                          />
                        ))}
                        <div
                          style={{
                            marginTop: 9,
                            background: C.amberL,
                            border: '1px solid #f5e6c8',
                            borderRadius: 10,
                            padding: '2px 11px',
                          }}
                        >
                          <Toggle
                            label="Autoriser à voir le bénéfice des chantiers"
                            sub="Sinon : bénéfice, marge ET coût de la main d'œuvre masqués (non recalculables)."
                            on={getA(role, 'benefice', false)}
                            onChange={(v) => setA(role, 'benefice', v)}
                          />
                        </div>
                      </>
                    )}
                    {/* Au magasin, le même droit couvre le chiffre des ventes,
                        la marge et la valeur du stock. */}
                    {!avecBatiment && (
                      <div
                        style={{
                          marginTop: 9,
                          background: C.amberL,
                          border: '1px solid #f5e6c8',
                          borderRadius: 10,
                          padding: '2px 11px',
                        }}
                      >
                        <Toggle
                          label="Autoriser à voir le chiffre des ventes et la marge"
                          sub="Sinon : chiffre des ventes, marge et valeur du stock masqués."
                          on={getA(role, 'benefice', false)}
                          onChange={(v) => setA(role, 'benefice', v)}
                        />
                      </div>
                    )}
                    {(role === 'secretaire' || role === 'technicien' || role === 'directeur') && (
                      <div
                        style={{
                          marginTop: 9,
                          background: C.greenL,
                          border: '1px solid ' + C.green + '30',
                          borderRadius: 10,
                          padding: '2px 11px',
                        }}
                      >
                        <Toggle
                          label="Autoriser à encaisser les factures"
                          sub="Marquer payée, acompte, remboursement. Sans ce droit, le serveur refuse l'encaissement."
                          on={getA(role, 'paiement', true)}
                          onChange={(v) => setA(role, 'paiement', v)}
                        />
                      </div>
                    )}
                  </div>
                </Card>
              ))}
              <Card style={{ marginBottom: 11 }}>
                <Bar icon="∑" title="Comptable" color={C.green} sub="Accès complet aux chiffres" />
                <div style={{ padding: '11px 13px' }}>
                  <div style={{ fontSize: 9.5, color: C.grey, lineHeight: 1.6 }}>
                    La comptable voit <b>tout</b> : comptabilité, TVA, factures, paie, dépenses
                    {avecBatiment ? ', et la marge / le bénéfice de chaque chantier' : ''}. C'est
                    son métier.
                  </div>
                </div>
              </Card>
              <Card style={{ marginBottom: 11 }}>
                <Bar
                  icon="💰"
                  title="Droits — salaires"
                  color={C.purple}
                  sub="Qui peut voir les salaires des employés"
                />
                <div style={{ padding: '13px' }}>
                  <div style={{ fontSize: 9, color: C.muted, marginBottom: 10, lineHeight: 1.5 }}>
                    Le <b>patron</b> et le <b>comptable</b> voient toujours les salaires. Tu décides
                    pour le technicien et la secrétaire :
                  </div>
                  <Toggle
                    label="Technicien : voir les salaires"
                    on={!!(soc?.permSalaire || {}).technicien}
                    onChange={(v) =>
                      set({ permSalaire: { ...(soc?.permSalaire || {}), technicien: v } })
                    }
                    sub="Fiche de salaire · relevé mensuel · onglet paie"
                  />
                  <Toggle
                    label="Directeur des travaux : voir les salaires"
                    on={!!(soc?.permSalaire || {}).directeur}
                    onChange={(v) =>
                      set({ permSalaire: { ...(soc?.permSalaire || {}), directeur: v } })
                    }
                    sub="Fiche de salaire · relevé mensuel · onglet paie"
                  />
                  <Toggle
                    label="Secrétaire : voir les salaires"
                    on={!!(soc?.permSalaire || {}).secretaire}
                    onChange={(v) =>
                      set({ permSalaire: { ...(soc?.permSalaire || {}), secretaire: v } })
                    }
                    sub="Fiche de salaire · relevé mensuel · onglet paie"
                  />
                  <div
                    style={{
                      marginTop: 11,
                      padding: '9px 12px',
                      background: C.tealL,
                      borderRadius: 8,
                      fontSize: 9,
                      color: C.grey,
                      lineHeight: 1.5,
                    }}
                  >
                    ✓ <b>Comptable</b> : accès complet aux salaires (non modifiable).
                    <br />
                    Sans autorisation, le technicien et la secrétaire voient les{' '}
                    <b>{avecBatiment ? 'heures et chantiers' : 'heures'}</b>, mais pas les montants.
                  </div>
                </div>
              </Card>
              <Card style={{ marginBottom: 11 }}>
                <Bar
                  icon="📦"
                  title="Droits — catalogue / stock"
                  color={C.navy}
                  sub="Qui peut voir et gérer le catalogue et le stock"
                />
                <div style={{ padding: '13px' }}>
                  <div style={{ fontSize: 9, color: C.muted, marginBottom: 10, lineHeight: 1.5 }}>
                    Par défaut, seuls le patron et la comptable accèdent au catalogue et au stock.
                    Tu peux l'ouvrir à :
                  </div>
                  <Toggle
                    label="Technicien : accès catalogue / stock"
                    on={!!(soc?.permStock || {}).technicien}
                    onChange={(v) =>
                      set({ permStock: { ...(soc?.permStock || {}), technicien: v } })
                    }
                    sub="Voir et gérer les articles"
                  />
                  <Toggle
                    label="Directeur des travaux : accès catalogue / stock"
                    on={!!(soc?.permStock || {}).directeur}
                    onChange={(v) =>
                      set({ permStock: { ...(soc?.permStock || {}), directeur: v } })
                    }
                    sub="Voir et gérer les articles"
                  />
                  <Toggle
                    label="Secrétaire : accès catalogue / stock"
                    on={!!(soc?.permStock || {}).secretaire}
                    onChange={(v) =>
                      set({ permStock: { ...(soc?.permStock || {}), secretaire: v } })
                    }
                    sub="Voir et gérer les articles"
                  />
                  <div
                    style={{
                      marginTop: 11,
                      padding: '9px 12px',
                      background: C.navyXL,
                      borderRadius: 8,
                      fontSize: 9,
                      color: C.grey,
                      lineHeight: 1.5,
                    }}
                  >
                    Sans autorisation, ces rôles ne voient pas le catalogue/stock dans leur menu.
                  </div>
                </div>
              </Card>
              {/* Une police d'assurance, un extrait des poursuites : le patron
                  choisit qui les voit et les envoie. Le serveur applique le
                  même réglage (soc.permAttestations). */}
              <Card style={{ marginBottom: 11 }}>
                <Bar
                  icon="🏢"
                  title={tr('Droits — attestations de l’entreprise')}
                  color={C.navy}
                  sub={tr('Voir, ajouter et envoyer RC, registre du commerce, poursuites…')}
                />
                <div style={{ padding: '13px' }}>
                  {[
                    ['👑 ' + tr('Patron'), tr('Toujours'), tr('Toujours')],
                    [
                      '∑ ' + tr('Comptable'),
                      tr('Toujours : elle en a besoin pour la comptabilité'),
                      tr('Toujours'),
                    ],
                  ].map(([role, sub, etat]) => (
                    <div
                      key={role}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '9px 0',
                        borderBottom: '1px solid ' + C.border,
                      }}
                    >
                      <div style={{ flex: 1, marginRight: 10 }}>
                        <div style={{ fontSize: 10, fontWeight: 600 }}>{role}</div>
                        <div style={{ fontSize: 8, color: C.muted }}>{sub}</div>
                      </div>
                      <span
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          color: C.green,
                          background: C.greenL,
                          borderRadius: 7,
                          padding: '3px 8px',
                        }}
                      >
                        ✓ {etat}
                      </span>
                    </div>
                  ))}
                  <Toggle
                    label={'🛠 ' + tr('Technicien')}
                    on={!!(soc?.permAttestations || {}).technicien}
                    onChange={(v) =>
                      set({
                        permAttestations: { ...(soc?.permAttestations || {}), technicien: v },
                      })
                    }
                    sub={tr('Voir et envoyer les attestations')}
                  />
                  <Toggle
                    label={'🧭 ' + tr('Directeur des travaux')}
                    on={!!(soc?.permAttestations || {}).directeur}
                    onChange={(v) =>
                      set({
                        permAttestations: { ...(soc?.permAttestations || {}), directeur: v },
                      })
                    }
                    sub={tr('Voir et envoyer les attestations')}
                  />
                  <Toggle
                    label={'📋 ' + tr('Secrétaire')}
                    on={!!(soc?.permAttestations || {}).secretaire}
                    onChange={(v) =>
                      set({
                        permAttestations: { ...(soc?.permAttestations || {}), secretaire: v },
                      })
                    }
                    sub={tr('Voir et envoyer les attestations')}
                  />
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '9px 0',
                    }}
                  >
                    <div style={{ flex: 1, marginRight: 10 }}>
                      <div style={{ fontSize: 10, fontWeight: 600 }}>👷 {tr('Ouvrier')}</div>
                      <div style={{ fontSize: 8, color: C.muted }}>{tr('Jamais')}</div>
                    </div>
                    <span style={{ fontSize: 12, color: C.muted }}>—</span>
                  </div>
                  <div
                    style={{
                      marginTop: 11,
                      padding: '9px 12px',
                      background: C.navyXL,
                      borderRadius: 8,
                      fontSize: 9,
                      color: C.grey,
                      lineHeight: 1.5,
                    }}
                  >
                    {tr(
                      'Sans autorisation, le technicien et la secrétaire ne voient pas la carte « Attestations » ni les cases « Joindre des attestations » dans l’envoi d’une offre.',
                    )}
                  </div>
                </div>
              </Card>
              {codeOuvrier && (
                <Card style={{ marginBottom: 11, border: '2px solid ' + C.teal }}>
                  {/*
                    Cette carte montrait un code fabrique a l'ecran, pour un
                    parcours « Rejoindre mon equipe » qui n'existe pas : le patron
                    le donnait a ses ouvriers, et l'ecran de connexion repondait
                    « Code d'entreprise inconnu ». Elle montre le code que le
                    serveur attribue, le seul que la connexion reconnaisse.
                  */}
                  <Bar
                    icon="🔑"
                    title="Code de ta société"
                    color={C.teal}
                    sub="À communiquer à tes ouvriers pour qu'ils se connectent"
                  />
                  <div style={{ padding: '13px' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: C.navyXL,
                        borderRadius: 11,
                        padding: '14px 16px',
                      }}
                    >
                      <div
                        style={{
                          fontSize: 24,
                          fontWeight: 900,
                          color: C.navy,
                          letterSpacing: 3,
                          fontFamily: 'monospace',
                        }}
                      >
                        {soc?.codeOuvrier || '—'}
                      </div>
                      <Btn
                        label="📋 Copier"
                        sm
                        color={C.teal}
                        disabled={!soc?.codeOuvrier}
                        onClick={() => {
                          try {
                            if (navigator && navigator.clipboard)
                              navigator.clipboard.writeText(soc?.codeOuvrier || '');
                            if (typeof window !== 'undefined' && window.alert)
                              window.alert('Code copié : ' + (soc?.codeOuvrier || ''));
                          } catch (e) {}
                        }}
                      />
                    </div>
                    <div style={{ fontSize: 9, color: C.muted, marginTop: 9, lineHeight: 1.6 }}>
                      Sur l’écran de connexion, tes ouvriers choisissent <b>« Code ouvrier »</b>,
                      saisissent ce code, puis leur nom et leur code à quatre chiffres — celui que
                      tu leur donnes sur leur fiche, dans Équipe & paie.
                    </div>
                  </div>
                </Card>
              )}
              <Card style={{ marginBottom: 11 }}>
                <Bar
                  icon="👥"
                  title="Équipe & accès"
                  color={C.navy}
                  sub="Ajoute tes collaborateurs — chacun reçoit ses accès selon son rôle"
                />
                <div style={{ padding: 13 }}>
                  <div
                    style={{
                      background: C.navyXL,
                      borderRadius: 9,
                      padding: '8px 11px',
                      fontSize: 9,
                      color: C.grey,
                      lineHeight: 1.5,
                      marginBottom: 10,
                    }}
                  >
                    {codeOuvrier
                      ? "🔑 Technicien, comptable et secrétaire se connectent avec un compte e-mail et un mot de passe, créés depuis leur fiche dans Équipe (« Accès »). Le code PIN n'ouvre que l'espace ouvrier (« Code ouvrier » à la connexion)."
                      : "🔑 Chacun, ouvrier compris, se connecte avec son adresse e-mail et son mot de passe, choisis en acceptant l'invitation envoyée depuis sa fiche dans Équipe (« Inviter »). À chaque connexion, un code à 6 chiffres lui est ensuite envoyé par e-mail ou par SMS."}
                  </div>
                  {!accForm ? (
                    <div
                      onClick={() =>
                        setAccForm({
                          prenom: '',
                          nom: '',
                          tel: '',
                          email: '',
                          role: 'technicien',
                          pin: '',
                          adresse: '',
                          npa: '',
                          ville: '',
                          avs: '',
                          dateEmbauche: '',
                        })
                      }
                      style={{
                        background: C.navy,
                        borderRadius: 12,
                        padding: 14,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                        marginBottom: 4,
                      }}
                    >
                      <span style={{ fontSize: 20, color: '#fff' }}>＋</span>
                      <span style={{ fontSize: 13, fontWeight: 800, color: '#fff' }}>
                        Ajouter un collaborateur
                      </span>
                    </div>
                  ) : (
                    <div
                      style={{
                        border: '2px solid ' + C.navy,
                        borderRadius: 12,
                        padding: 13,
                        marginBottom: 6,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 800,
                          color: C.navy,
                          marginBottom: 9,
                          textTransform: 'uppercase',
                          letterSpacing: 0.4,
                        }}
                      >
                        {accForm.id ? 'Modifier le collaborateur' : 'Nouveau collaborateur'}
                      </div>
                      <div style={{ display: 'flex', gap: 7, marginBottom: 8 }}>
                        <input
                          value={accForm.prenom}
                          onChange={(e) => setAccForm((p) => ({ ...p, prenom: e.target.value }))}
                          placeholder="Prénom"
                          style={{ ..._inp }}
                        />
                        <input
                          value={accForm.nom}
                          onChange={(e) => setAccForm((p) => ({ ...p, nom: e.target.value }))}
                          placeholder="Nom"
                          style={{ ..._inp }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 7, marginBottom: 8 }}>
                        <input
                          value={accForm.tel}
                          onChange={(e) => setAccForm((p) => ({ ...p, tel: e.target.value }))}
                          placeholder="Téléphone"
                          style={{ ..._inp }}
                        />
                        <input
                          value={accForm.email}
                          onChange={(e) => setAccForm((p) => ({ ...p, email: e.target.value }))}
                          placeholder="E-mail"
                          style={{ ..._inp }}
                        />
                      </div>
                      <div
                        style={{
                          fontSize: 9,
                          fontWeight: 800,
                          color: C.muted,
                          textTransform: 'uppercase',
                          letterSpacing: 0.4,
                          margin: '4px 0 6px',
                        }}
                      >
                        Rôle — les accès sont attribués automatiquement
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 7,
                          marginBottom: 9,
                        }}
                      >
                        {ROLES_ASSIGN.map(([r, ic, lb]) => {
                          const on = accForm.role === r;
                          return (
                            <div
                              key={r}
                              onClick={() => setAccForm((p) => ({ ...p, role: r }))}
                              style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 9,
                                border: '1.5px solid ' + (on ? C.navy : C.border),
                                background: on ? C.navyXL : C.white,
                                borderRadius: 10,
                                padding: '10px 11px',
                                cursor: 'pointer',
                              }}
                            >
                              <span style={{ fontSize: 17, lineHeight: 1.1 }}>{ic}</span>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 10.5, fontWeight: 800, color: C.ink }}>
                                  {lb}
                                </div>
                                <div
                                  style={{
                                    fontSize: 8.5,
                                    color: C.grey,
                                    lineHeight: 1.4,
                                    marginTop: 2,
                                  }}
                                >
                                  {ROLE_DESC[r]}
                                </div>
                              </div>
                              {on && (
                                <span style={{ color: C.navy, fontWeight: 900, fontSize: 14 }}>
                                  ✓
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <div
                        style={{
                          fontSize: 9,
                          fontWeight: 800,
                          color: C.muted,
                          textTransform: 'uppercase',
                          letterSpacing: 0.4,
                          margin: '4px 0 6px',
                        }}
                      >
                        Paie — pour générer sa fiche de salaire
                      </div>
                      <div style={{ display: 'flex', gap: 7, marginBottom: 8 }}>
                        <input
                          value={accForm.salBase}
                          onChange={(e) =>
                            setAccForm((p) => ({
                              ...p,
                              salBase: e.target.value.replace(',', '.').replace(/[^\d.]/g, ''),
                            }))
                          }
                          placeholder="Salaire brut / mois"
                          inputMode="decimal"
                          style={{ ..._inp }}
                        />
                        <input
                          value={accForm.txH}
                          onChange={(e) =>
                            setAccForm((p) => ({
                              ...p,
                              txH: e.target.value.replace(',', '.').replace(/[^\d.]/g, ''),
                            }))
                          }
                          placeholder="Taux horaire"
                          inputMode="decimal"
                          style={{ ..._inp }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 7, marginBottom: 8 }}>
                        <input
                          value={accForm.iban}
                          onChange={(e) => setAccForm((p) => ({ ...p, iban: e.target.value }))}
                          placeholder="IBAN (versement salaire)"
                          style={{ ..._inp }}
                        />
                      </div>
                      <div
                        style={{
                          fontSize: 9,
                          fontWeight: 800,
                          color: C.muted,
                          textTransform: 'uppercase',
                          letterSpacing: 0.4,
                          margin: '4px 0 6px',
                        }}
                      >
                        Coordonnées — pour les contrats & la convention
                      </div>
                      <input
                        value={accForm.adresse || ''}
                        onChange={(e) => setAccForm((p) => ({ ...p, adresse: e.target.value }))}
                        placeholder="Adresse (rue + n°)"
                        style={{ ..._inp, marginBottom: 8 }}
                      />
                      <div style={{ display: 'flex', gap: 7, marginBottom: 8 }}>
                        <input
                          value={accForm.npa || ''}
                          onChange={(e) => setAccForm((p) => ({ ...p, npa: e.target.value }))}
                          placeholder="NPA"
                          style={{ ..._inp, flex: '0 0 90px' }}
                        />
                        <input
                          value={accForm.ville || ''}
                          onChange={(e) => setAccForm((p) => ({ ...p, ville: e.target.value }))}
                          placeholder="Ville"
                          style={{ ..._inp }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 7, marginBottom: 8 }}>
                        <input
                          value={accForm.avs || ''}
                          onChange={(e) => setAccForm((p) => ({ ...p, avs: e.target.value }))}
                          placeholder="N° AVS (756.xxxx.xxxx.xx)"
                          style={{ ..._inp }}
                        />
                        <input
                          type="date"
                          value={accForm.dateEmbauche || ''}
                          onChange={(e) =>
                            setAccForm((p) => ({ ...p, dateEmbauche: e.target.value }))
                          }
                          title="Date d'embauche"
                          style={{ ..._inp }}
                        />
                      </div>
                      {codeOuvrier && (
                        <div
                          style={{
                            display: 'flex',
                            gap: 7,
                            alignItems: 'center',
                            marginBottom: 10,
                          }}
                        >
                          <div style={{ fontSize: 10, fontWeight: 700, color: C.ink }}>
                            Code PIN (4 chiffres)
                          </div>
                          <input
                            value={accForm.pin}
                            maxLength={4}
                            inputMode="numeric"
                            placeholder="••••"
                            onChange={(e) =>
                              setAccForm((p) => ({ ...p, pin: e.target.value.replace(/\D/g, '') }))
                            }
                            style={{
                              width: 70,
                              border: '1px solid ' + C.border,
                              borderRadius: 8,
                              padding: '8px',
                              fontSize: 12,
                              textAlign: 'center',
                              letterSpacing: 3,
                            }}
                          />
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 7 }}>
                        <Btn
                          label={accForm.id ? '✓ Enregistrer' : '✓ Ajouter'}
                          color={C.green}
                          full
                          disabled={!accForm.prenom.trim() || !accForm.nom.trim()}
                          onClick={() => {
                            const nomComplet = (accForm.prenom + ' ' + accForm.nom).trim();
                            const roleLabel =
                              {
                                technicien: 'Technicien',
                                comptable: 'Comptable / Fiduciaire',
                                secretaire: 'Secrétaire',
                                ouvrier: 'Employé',
                              }[accForm.role] || 'Collaborateur';
                            if (accForm.id) {
                              updMembre(accForm.id, accForm);
                              if (accForm.empId && setEmployes)
                                setEmployes((p) =>
                                  p.map((e) =>
                                    e.id === accForm.empId
                                      ? {
                                          ...e,
                                          nom: nomComplet,
                                          tel: accForm.tel,
                                          salBase: +accForm.salBase || e.salBase,
                                          txH: +accForm.txH || e.txH,
                                          iban: accForm.iban || e.iban,
                                          adresse:
                                            accForm.adresse !== undefined
                                              ? accForm.adresse
                                              : e.adresse,
                                          npa: accForm.npa !== undefined ? accForm.npa : e.npa,
                                          ville:
                                            accForm.ville !== undefined ? accForm.ville : e.ville,
                                          avs: accForm.avs !== undefined ? accForm.avs : e.avs,
                                          dateEmbauche:
                                            accForm.dateEmbauche !== undefined
                                              ? accForm.dateEmbauche
                                              : e.dateEmbauche,
                                          ...codePinSaisi(accForm.pin),
                                        }
                                      : e,
                                  ),
                                );
                            } else {
                              let newEmpId = null;
                              if (setEmployes) {
                                setEmployes((p) => {
                                  newEmpId = idProvisoire();
                                  return [
                                    ...p,
                                    {
                                      id: newEmpId,
                                      nom: nomComplet,
                                      role: roleLabel,
                                      tel: accForm.tel,
                                      txH: +accForm.txH || 50,
                                      salBase: +accForm.salBase || 5000,
                                      conges: 25,
                                      lpp: 5,
                                      is: 0,
                                      sal13: false,
                                      iban: accForm.iban || '',
                                      adresse: accForm.adresse || '',
                                      npa: accForm.npa || '',
                                      ville: accForm.ville || '',
                                      avs: accForm.avs || '',
                                      dateEmbauche: accForm.dateEmbauche || '',
                                      ...codePinSaisi(accForm.pin),
                                      col: EMP_COLORS[
                                        Math.floor(Math.random() * EMP_COLORS.length)
                                      ],
                                      heuresJour: 8,
                                    },
                                  ];
                                });
                              }
                              addMembre({ ...accForm, empId: newEmpId });
                            }
                            setAccForm(null);
                          }}
                        />
                        <Btn
                          label="Annuler"
                          ghost
                          color={C.grey}
                          onClick={() => setAccForm(null)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </Card>

              {membres.length > 0 && (
                <Card style={{ marginBottom: 11 }}>
                  <Bar icon="📋" title={'Collaborateurs (' + membres.length + ')'} color={C.teal} />
                  <div style={{ padding: '6px 13px 12px' }}>
                    {ROLES_ASSIGN.map(([r, ic, lb]) => {
                      const list = membres.filter((m) => m.role === r);
                      if (!list.length) return null;
                      return (
                        <div key={r} style={{ marginBottom: 10 }}>
                          <div
                            style={{
                              fontSize: 8.5,
                              fontWeight: 800,
                              color: C.muted,
                              textTransform: 'uppercase',
                              letterSpacing: 0.5,
                              margin: '6px 0 5px',
                            }}
                          >
                            {ic} {lb} · {list.length}
                          </div>
                          {list.map((m) => (
                            <div
                              key={m.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                border: '1px solid ' + C.border,
                                borderRadius: 10,
                                padding: '10px 12px',
                                marginBottom: 6,
                              }}
                            >
                              <div
                                style={{
                                  width: 34,
                                  height: 34,
                                  borderRadius: 9,
                                  background: C.navy + '14',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: 13,
                                  fontWeight: 900,
                                  color: C.navy,
                                  flexShrink: 0,
                                }}
                              >
                                {(m.prenom || '?').charAt(0)}
                                {(m.nom || '').charAt(0)}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 11.5, fontWeight: 800, color: C.ink }}>
                                  {m.prenom} {m.nom}
                                </div>
                                <div style={{ fontSize: 8.5, color: C.muted }}>
                                  {[m.tel, m.email].filter(Boolean).join(' · ') || '—'}
                                </div>
                              </div>
                              {codeOuvrier && (
                                <div
                                  style={{
                                    fontSize: 9,
                                    fontWeight: 800,
                                    color: C.grey,
                                    background: C.navyXL,
                                    borderRadius: 6,
                                    padding: '3px 7px',
                                    marginRight: 4,
                                  }}
                                >
                                  {codePinDuMembre(m) ? 'PIN ✓' : 'PIN —'}
                                </div>
                              )}
                              <span
                                onClick={() => {
                                  var _e =
                                    (employes || []).find(function (x) {
                                      return String(x.id) === String(m.empId);
                                    }) || {};
                                  setAccForm(
                                    Object.assign({}, m, {
                                      // Vide : un code saisi remplace celui de
                                      // la fiche, un champ laissé vide le garde.
                                      pin: '',
                                      adresse: _e.adresse || '',
                                      npa: _e.npa || '',
                                      ville: _e.ville || '',
                                      avs: _e.avs || '',
                                      dateEmbauche: _e.dateEmbauche || '',
                                    }),
                                  );
                                }}
                                style={{ cursor: 'pointer', fontSize: 14, marginRight: 6 }}
                              >
                                ✎
                              </span>
                              <span
                                onClick={() => {
                                  if (
                                    typeof window === 'undefined' ||
                                    !window.confirm ||
                                    window.confirm('Retirer ' + m.prenom + ' ' + m.nom + ' ?')
                                  )
                                    delMembre(m.id);
                                }}
                                style={{ cursor: 'pointer', fontSize: 13, color: C.red }}
                              >
                                🗑
                              </span>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </Card>
              )}
            </div>
          );
        })()}

      {tab === 'pref' && (
        <div>
          {/*
            Le « Mode d'activité » cliquable et « Revoir l'écran de
            configuration de départ » ont quitté les préférences : le module
            fixe le tarif et ce que la société peut ouvrir, il ne se change
            que par l'abonnement (carte « Secteur d'activité » de l'onglet
            Société, en lecture seule). L'écran de départ repassait par
            l'inscription d'abonnement, que le serveur refuse à un abonné ;
            les coordonnées se modifient dans l'onglet Société.
          */}
          <Card style={{ marginBottom: 11 }}>
            <Bar
              icon="🧰"
              title="Mon activité"
              color={C.teal}
              sub="Tes unités et catégories — pour n'importe quel métier"
            />
            <div style={{ padding: 13 }}>
              {(() => {
                const estMagU = estMixte ? univers === 'magasin' : soc?.secteur === 'magasin';
                const champ = estMagU ? 'unitesMag' : 'unites';
                return (
                  <>
                    <div style={{ fontSize: 9, fontWeight: 700, color: C.muted, marginBottom: 6 }}>
                      UNITÉS{' '}
                      {estMagU
                        ? '(commerce — pièce, bouteille, litre…)'
                        : '(bâtiment — m², ml, m³…)'}
                    </div>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 10 }}>
                      {metierUnites(soc, estMagU).map((u, i) => (
                        <span
                          key={u + i}
                          style={{
                            fontSize: 9.5,
                            fontWeight: 700,
                            background: C.navyXL,
                            color: C.navy,
                            borderRadius: 99,
                            padding: '5px 9px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                          }}
                        >
                          {u}
                          <span
                            onClick={() =>
                              set({ [champ]: metierUnites(soc, estMagU).filter((x, j) => j !== i) })
                            }
                            style={{ cursor: 'pointer', color: '#c0392b', fontWeight: 900 }}
                          >
                            ✕
                          </span>
                        </span>
                      ))}
                      <span
                        onClick={async () => {
                          try {
                            const v = await demande(
                              estMagU
                                ? 'Nouvelle unité (ex. pièce, bouteille, pack, litre, kg…) :'
                                : 'Nouvelle unité (ex. m², ml, m³, h, forfait…) :',
                            );
                            const unites = metierUnites(soc, estMagU);
                            const apres = avecSansDoublon(unites, v);
                            if (apres !== unites) set({ [champ]: apres });
                          } catch (e) {}
                        }}
                        style={{
                          fontSize: 9.5,
                          fontWeight: 800,
                          background: C.teal,
                          color: '#fff',
                          borderRadius: 99,
                          padding: '5px 11px',
                          cursor: 'pointer',
                        }}
                      >
                        ＋ unité
                      </span>
                    </div>
                  </>
                );
              })()}
              <div style={{ fontSize: 9, fontWeight: 700, color: C.muted, margin: '2px 0 6px' }}>
                CATÉGORIES (dépenses / factures fournisseurs)
              </div>
              {(() => {
                const estMagC = estMixte ? univers === 'magasin' : soc?.secteur === 'magasin';
                const champC = estMagC ? 'catsMag' : 'cats';
                return (
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                    {metierCats(soc, estMagC).map((cat, i) => (
                      <span
                        key={cat + i}
                        style={{
                          fontSize: 9.5,
                          fontWeight: 700,
                          background: C.white,
                          border: '1px solid ' + C.border,
                          color: C.ink,
                          borderRadius: 99,
                          padding: '5px 9px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        {cat}
                        <span
                          onClick={() =>
                            set({ [champC]: metierCats(soc, estMagC).filter((x, j) => j !== i) })
                          }
                          style={{ cursor: 'pointer', color: '#c0392b', fontWeight: 900 }}
                        >
                          ✕
                        </span>
                      </span>
                    ))}
                    <span
                      onClick={async () => {
                        try {
                          const v = await demande('Nouvelle catégorie :');
                          const cats = metierCats(soc, estMagC);
                          const apres = avecSansDoublon(cats, v);
                          if (apres !== cats) set({ [champC]: apres });
                        } catch (e) {}
                      }}
                      style={{
                        fontSize: 9.5,
                        fontWeight: 800,
                        background: C.teal,
                        color: '#fff',
                        borderRadius: 99,
                        padding: '5px 11px',
                        cursor: 'pointer',
                      }}
                    >
                      ＋ catégorie
                    </span>
                  </div>
                );
              })()}
              {(() => {
                const estMagV = estMixte ? univers === 'magasin' : soc?.secteur === 'magasin';
                return (
                  <>
                    <div
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        color: C.muted,
                        margin: '14px 0 6px',
                      }}
                    >
                      CORPS DE MÉTIER / RAYONS (tes articles & catalogue)
                    </div>
                    {(() => {
                      const estMagR = estMixte ? univers === 'magasin' : soc?.secteur === 'magasin';
                      const champR = estMagR ? 'rayonsMag' : 'rayonsBat';
                      const list = metierRayons(soc, estMagR);
                      return (
                        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                          {list.map((r, i) => (
                            <span
                              key={r + i}
                              style={{
                                fontSize: 9.5,
                                fontWeight: 700,
                                background: C.navyXL,
                                color: C.navy,
                                borderRadius: 99,
                                padding: '5px 9px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                              }}
                            >
                              {r}
                              <span
                                onClick={() => set({ [champR]: list.filter((x, j) => j !== i) })}
                                style={{ cursor: 'pointer', color: '#c0392b', fontWeight: 900 }}
                              >
                                ✕
                              </span>
                            </span>
                          ))}
                          <span
                            onClick={async () => {
                              try {
                                const v = await demande(
                                  estMagR
                                    ? 'Nouveau corps de métier / rayon (ex. Menuiserie, Toiture, Jardinage…) :'
                                    : 'Nouveau corps de métier (ex. Étanchéité, Serrurerie…) :',
                                );
                                const apres = avecSansDoublon(list, v);
                                if (apres !== list) set({ [champR]: apres });
                              } catch (e) {}
                            }}
                            style={{
                              fontSize: 9.5,
                              fontWeight: 800,
                              background: C.green,
                              color: '#fff',
                              borderRadius: 99,
                              padding: '5px 11px',
                              cursor: 'pointer',
                            }}
                          >
                            ＋ corps de métier
                          </span>
                        </div>
                      );
                    })()}
                    <div
                      style={{ fontSize: 8, color: C.muted, margin: '4px 0 2px', lineHeight: 1.5 }}
                    >
                      Ajoute ici un corps de métier <b>sans passer par un article</b>. Il apparaîtra
                      comme bouton quand tu ajoutes un article et regroupera tes articles.
                    </div>
                    <div
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        color: C.muted,
                        margin: '12px 0 4px',
                      }}
                    >
                      VOCABULAIRE
                    </div>
                    <Inp
                      label={
                        estMagV ? 'Nom de tes « points de vente »' : 'Nom de tes « chantiers »'
                      }
                      value={soc?.motChantier || ''}
                      onChange={(e) => set({ motChantier: e.target.value })}
                      placeholder={
                        estMagV
                          ? 'Points de vente (ou Magasins, Boutiques…)'
                          : 'Chantiers (ou Interventions, Projets, Commandes…)'
                      }
                    />
                    <div style={{ fontSize: 8, color: C.muted, marginTop: 4 }}>
                      Ces unités, catégories et le vocabulaire s'appliquent dans l'app.
                      Renomme/ajoute/supprime librement selon ton activité.
                    </div>
                  </>
                );
              })()}
            </div>
          </Card>
          <Card style={{ marginBottom: 11 }}>
            <Bar
              icon="💾"
              title="Sauvegarde de tes données"
              color={C.green}
              sub="Enregistrement automatique sur cet appareil"
            />
            <div style={{ padding: 13 }}>
              <div
                style={{
                  background: C.greenL,
                  borderRadius: 9,
                  padding: '9px 11px',
                  fontSize: 9,
                  color: C.grey,
                  marginBottom: 10,
                }}
              >
                ✓ Tes{' '}
                {avecBatiment
                  ? 'devis, factures, clients et chantiers'
                  : 'offres, factures, clients et ventes'}{' '}
                sont enregistrés sur le serveur à chaque modification. Ils sont donc conservés même
                en cas de perte de l'appareil et partagés entre tous les postes de l'entreprise.
              </div>
              <Btn
                label="⬇️ Exporter une sauvegarde (.json)"
                color={C.navy}
                full
                onClick={async () => {
                  // Le serveur assemble toutes les collections de la société :
                  // le fichier ne contenait auparavant que la fiche société.
                  try {
                    await telechargeLaSauvegarde();
                  } catch (e) {
                    alert(e?.message || 'Export indisponible pour le moment.');
                  }
                }}
              />
              <div style={{ fontSize: 8.5, color: C.muted, marginTop: 8, lineHeight: 1.5 }}>
                Le fichier contient toutes les données de la société : clients, offres, factures,
                chantiers, équipe (paie, AVS et IBAN compris), stock, catalogue, dépenses, ventes,
                commandes, livraisons, rendez-vous, feuilles et congés. Réservé au patron ; garde-le
                en lieu sûr.
              </div>
            </div>
          </Card>
          <Card style={{ marginBottom: 11 }}>
            <Bar
              icon="🌍"
              title="Langue"
              color={C.navy}
              sub="Celle du logiciel (patron et ouvriers) et des documents"
            />
            <div style={{ padding: 13 }}>
              {/* Seules les langues entièrement traduites : une langue à moitié
                  traduite mélangeait le français et l'autre langue. */}
              {LANGS_REG.filter(([code]) => LANGUES_TRADUITES.includes(code)).map(([code, lb]) => {
                const on = (soc?.lang || 'fr') === code;
                return (
                  <div
                    key={code}
                    onClick={() => {
                      set({ lang: code });
                      setLangGlobale(code);
                      onForceRefresh && onForceRefresh();
                    }}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '11px 12px',
                      border: '2px solid ' + (on ? C.navy : C.border),
                      borderRadius: 10,
                      marginBottom: 8,
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{ fontSize: 11, fontWeight: 700 }}>{lb}</span>
                    {on && <span style={{ color: C.navy, fontWeight: 900 }}>✓</span>}
                  </div>
                );
              })}
              <div style={{ fontSize: 9, color: C.green, fontWeight: 700, marginTop: 2 }}>
                ✓ Le logiciel et tes documents sont en{' '}
                {(LANGS_REG.find(([c]) => c === (soc?.lang || 'fr')) || ['', 'Français'])[1]}.
              </div>
            </div>
          </Card>
          <Card>
            <Bar
              icon="🌍"
              title="Pays"
              color={C.navy}
              sub="Définit la devise, les taux de TVA et le format de paiement"
            />
            <div style={{ padding: 13 }}>
              {(() => {
                const cur = PAYS_BY[soc?.pays] || PAYS_BY['CH'];
                const selP = paysSel ? PAYS_BY[paysSel] : null;
                const diff = selP && paysSel !== (soc?.pays || 'CH');
                return (
                  <div
                    style={{
                      background: C.navyXL,
                      borderRadius: 9,
                      padding: '9px 11px',
                      marginBottom: 9,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 8, color: C.muted, textTransform: 'uppercase' }}>
                          Pays enregistré
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 800, color: C.navy }}>
                          {cur.nom}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', fontSize: 9, color: C.grey }}>
                        Devise {cur.devise}
                        <br />
                        TVA{' '}
                        {tauxTvaProposes(soc)
                          .filter((t) => t > 0)
                          .join(' / ') || '—'}
                        %
                      </div>
                    </div>
                    {diff && (
                      <div
                        style={{
                          fontSize: 9.5,
                          color: C.amber,
                          fontWeight: 700,
                          marginTop: 7,
                          borderTop: '1px dashed ' + C.border,
                          paddingTop: 7,
                        }}
                      >
                        Choix en attente : {selP.nom} ({selP.devise}) — appuie sur « Enregistrer »
                        ci-dessous.
                      </div>
                    )}
                  </div>
                );
              })()}
              <input
                value={paysQ}
                onChange={(e) => setPaysQ(e.target.value)}
                placeholder="🔎 Rechercher un pays…"
                style={{
                  width: '100%',
                  border: '1px solid ' + C.border,
                  borderRadius: 9,
                  padding: '10px',
                  fontSize: 11,
                  marginBottom: 8,
                }}
              />
              <div style={{ maxHeight: 230, overflowY: 'auto' }}>
                {PAYS.filter(
                  (p) => !paysQ || p.nom.toLowerCase().includes(paysQ.toLowerCase()),
                ).map((p) => {
                  const curC = soc?.pays || 'CH';
                  const sel = (paysSel || curC) === p.code;
                  return (
                    <div
                      key={p.code}
                      onClick={() => {
                        setPaysSel(p.code);
                        setPaysOk(false);
                      }}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px 11px',
                        border: '1.5px solid ' + (sel ? C.green : C.border),
                        borderRadius: 9,
                        marginBottom: 6,
                        cursor: 'pointer',
                        background: sel ? C.greenL : 'transparent',
                      }}
                    >
                      <span style={{ fontSize: 10.5, fontWeight: 700 }}>{p.nom}</span>
                      <span style={{ fontSize: 9, color: C.muted }}>
                        {p.devise} · TVA {p.tva.filter((t) => t > 0)[0] || 0}%
                        {p.code === curC ? '  ' + tr('(actuel)') : sel ? '  ✓' : ''}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: 10 }}>
                <Btn
                  label={paysOk ? '✓ Pays enregistré' : '✓ Enregistrer le pays'}
                  color={C.green}
                  full
                  onClick={() => {
                    const code = paysSel || soc?.pays || 'CH';
                    const p = PAYS_BY[code];
                    if (!p) return;
                    // La langue du pays si l'interface y est traduite ; sinon
                    // la langue choisie jusque-là.
                    const __lg = LANGUES_TRADUITES.includes(LANGUE_DU_PAYS[p.code])
                      ? LANGUE_DU_PAYS[p.code]
                      : soc?.lang || 'fr';
                    set({
                      pays: p.code,
                      devise: p.devise,
                      // QR code + IBAN dans les pays qui ont un QR code bancaire.
                      paiement: systemeQrDuPays(p.code) ? 'deux' : 'iban',
                      lang: __lg,
                    });
                    setDeviseGlobale(p.devise);
                    setPaysMontants(p.code);
                    setLangGlobale(__lg);
                    setPaysOk(true);
                    setPaysSel(null);
                    onForceRefresh && onForceRefresh();
                  }}
                />
                {paysOk && (
                  <div
                    style={{
                      fontSize: 9,
                      color: C.green,
                      fontWeight: 700,
                      textAlign: 'center',
                      marginTop: 6,
                    }}
                  >
                    ✓ Enregistré — devise et format de paiement mis à jour.
                  </div>
                )}
              </div>
              <div style={{ fontSize: 8, color: C.muted, marginTop: 10 }}>
                Les taux de TVA et leur date d'entrée en vigueur se règlent dans Documents → « Taux
                de TVA ». Le 0 % reste toujours disponible.
              </div>
            </div>
          </Card>
        </div>
      )}
      {/* « Faire découvrir SwissTech » n'a qu'une place : Plus → Partager
          l'application. */}
      <SaveBar label="✓ Enregistrer les réglages" color={C.teal} />
      <div
        style={{ fontSize: 8, color: C.muted, textAlign: 'center', marginTop: 2, marginBottom: 6 }}
      >
        Tout est sauvegardé automatiquement — ce bouton confirme l'enregistrement de cet onglet.
      </div>
    </Shell>
  );
}
